import { supabase } from '@/lib/supabase';
import { DetectionResult } from './mlServiceTypes';
import { calculateTextEntropy } from '../textUtils';
import { extractAdvancedFeatures } from '../analysis/advancedFeatureExtractor';
import { Json } from '@/integrations/supabase/types';

// Define proper types for the metadata
interface SampleMetadata {
  feedback_type: 'accurate' | 'false_positive' | 'false_negative';
  timestamp: string;
  original_model: string;
  text_length: number;
  entropy: number;
  advanced_metrics?: Record<string, any>;
  extracted_features?: {
    entropies?: Record<string, number>;
    complexity?: Record<string, number>;
    patterns?: Record<string, number>;
  };
  linguistic_features?: {
    lexical_diversity?: number;
    sentence_complexity?: number;
    readability_scores?: Record<string, number>;
    part_of_speech_distribution?: Record<string, number>;
    sentiment_scores?: Record<string, number>;
  };
}

/**
 * Service for collecting and storing training data for ML models
 */
export const dataCollector = {
  /**
   * Save a training sample with user feedback
   * @param text The original text that was analyzed
   * @param detectionResult The result from the detection system
   * @param feedback User feedback on the accuracy ('accurate', 'false_positive', 'false_negative')
   * @returns Success status
   */
  saveTrainingSample: async (
    text: string, 
    detectionResult: DetectionResult, 
    feedback: 'accurate' | 'false_positive' | 'false_negative'
  ): Promise<boolean> => {
    try {
      // Create a hash of the text to avoid duplicates
      const textHash = await createTextHash(text);
      
      // Extract advanced features
      const advancedFeatures = extractAdvancedFeatures(text);
      
      // Extract and process linguistic features
      const linguisticFeatures = extractLinguisticFeatures(text);
      
      // Prepare metadata with expanded feature set
      const metadata: SampleMetadata = {
        feedback_type: feedback,
        timestamp: new Date().toISOString(),
        original_model: detectionResult.modelName,
        text_length: text.length,
        entropy: calculateTextEntropy(text),
        advanced_metrics: detectionResult.textMetrics?.advancedMetrics,
        extracted_features: {
          entropies: advancedFeatures.entropies,
          complexity: advancedFeatures.complexity,
          patterns: advancedFeatures.patterns,
        },
        linguistic_features: linguisticFeatures
      };
      
      // Insert the training sample - Cast metadata to Json for compatibility
      const { error } = await supabase.from('ml_training_samples').insert({
        text_snippet: text.slice(0, 5000), // Limit length for storage
        text_hash: textHash,
        original_score: detectionResult.score,
        original_confidence: detectionResult.confidence,
        original_flags: detectionResult.flags,
        user_feedback: feedback,
        metadata: metadata as unknown as Json
      });
      
      if (error) {
        console.error("Error saving training sample:", error);
        return false;
      }
      
      console.log("Successfully saved training sample with feedback:", feedback);
      return true;
    } catch (error) {
      console.error("Failed to save training sample:", error);
      return false;
    }
  },

  /**
   * Get statistics about the training data
   * @returns Statistics about collected training samples
   */
  getTrainingStats: async () => {
    try {
      // Get total count
      const { count: totalCount, error: countError } = await supabase
        .from('ml_training_samples')
        .select('*', { count: 'exact', head: true });
      
      if (countError) throw countError;
      
      // Get distribution of feedback types
      const { data: distributionData, error: distributionError } = await supabase
        .from('ml_training_samples')
        .select('user_feedback');
        
      if (distributionError) throw distributionError;
      
      // Get new samples in last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { count: recentCount, error: recentError } = await supabase
        .from('ml_training_samples')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());
        
      if (recentError) throw recentError;
      
      // Calculate distribution
      const distribution = {
        accurate: 0,
        false_positive: 0,
        false_negative: 0
      };
      
      distributionData?.forEach(item => {
        if (item.user_feedback === 'accurate') distribution.accurate++;
        else if (item.user_feedback === 'false_positive') distribution.false_positive++;
        else if (item.user_feedback === 'false_negative') distribution.false_negative++;
      });
      
      // Get feature information summary
      const { data: featureSummaryData, error: featureSummaryError } = await supabase
        .from('ml_training_samples')
        .select('metadata')
        .limit(100)
        .order('created_at', { ascending: false });
        
      if (featureSummaryError) throw featureSummaryError;
      
      // Calculate feature coverage statistics
      const featureCoverage = {
        hasAdvancedMetrics: 0,
        hasExtractedFeatures: 0,
        hasLinguisticFeatures: 0,
        hasSentimentAnalysis: 0,
        totalSamples: featureSummaryData?.length || 0
      };
      
      featureSummaryData?.forEach(item => {
        // Type assertion for metadata to handle conversion safely
        const rawMetadata = item.metadata;
        // First convert to unknown, then to our expected type
        const metadata = rawMetadata as unknown as SampleMetadata;
        
        if (metadata?.advanced_metrics) featureCoverage.hasAdvancedMetrics++;
        if (metadata?.extracted_features) featureCoverage.hasExtractedFeatures++;
        if (metadata?.linguistic_features) featureCoverage.hasLinguisticFeatures++;
        if (metadata?.linguistic_features?.sentiment_scores) featureCoverage.hasSentimentAnalysis++;
      });
      
      return {
        totalSamples: totalCount || 0,
        last30DaysGrowth: recentCount || 0,
        distribution,
        featureCoverage: {
          advancedMetricsPercentage: featureCoverage.totalSamples > 0 ? 
            (featureCoverage.hasAdvancedMetrics / featureCoverage.totalSamples * 100).toFixed(1) : '0',
          extractedFeaturesPercentage: featureCoverage.totalSamples > 0 ? 
            (featureCoverage.hasExtractedFeatures / featureCoverage.totalSamples * 100).toFixed(1) : '0',
          linguisticFeaturesPercentage: featureCoverage.totalSamples > 0 ? 
            (featureCoverage.hasLinguisticFeatures / featureCoverage.totalSamples * 100).toFixed(1) : '0',
          sentimentAnalysisPercentage: featureCoverage.totalSamples > 0 ? 
            (featureCoverage.hasSentimentAnalysis / featureCoverage.totalSamples * 100).toFixed(1) : '0',
        },
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error("Error getting training stats:", error);
      return null;
    }
  },
  
  /**
   * Extract feature importance from training data
   * @returns Feature importance analysis
   */
  analyzeFeatureImportance: async () => {
    try {
      // Get samples with user feedback and advanced features
      const { data, error } = await supabase
        .from('ml_training_samples')
        .select('user_feedback, metadata')
        .limit(1000);
        
      if (error) throw error;
      
      if (!data || data.length === 0) {
        return { error: "Not enough data for feature analysis" };
      }
      
      // Simple feature correlation analysis
      const features: { [key: string]: { truePositive: number, falsePositive: number, correlation: number, samplesCount: number } } = {};
      
      // Track features in fraudulent vs non-fraudulent content
      data.forEach(sample => {
        // Type assertion for metadata to handle conversion safely
        const rawMetadata = sample.metadata;
        // First convert to unknown, then to our expected type
        const metadata = rawMetadata as unknown as SampleMetadata;
        
        if (!metadata) return;
        
        const advancedMetrics = metadata.advanced_metrics || {};
        const extractedFeatures = metadata.extracted_features || {};
        
        if (!advancedMetrics && !extractedFeatures) return;
        
        // Process each feature and calculate correlation with user feedback
        if (advancedMetrics) {
          Object.entries(advancedMetrics).forEach(([key, value]) => {
            if (typeof value === 'number') {
              if (!features[key]) {
                features[key] = { truePositive: 0, falsePositive: 0, correlation: 0, samplesCount: 0 };
              }
              
              features[key].samplesCount++;
              
              if (sample.user_feedback === 'accurate') {
                features[key].truePositive += value > 0.5 ? 1 : 0;
              } else if (sample.user_feedback === 'false_positive') {
                features[key].falsePositive += value > 0.5 ? 1 : 0;
              }
            }
          });
        }
        
        // Process extracted features
        if (extractedFeatures) {
          // Process entropies
          if (extractedFeatures.entropies) {
            Object.entries(extractedFeatures.entropies).forEach(([key, value]) => {
              const featureKey = `entropy_${key}`;
              if (!features[featureKey]) {
                features[featureKey] = { truePositive: 0, falsePositive: 0, correlation: 0, samplesCount: 0 };
              }
              
              features[featureKey].samplesCount++;
              
              if (sample.user_feedback === 'accurate') {
                features[featureKey].truePositive += (value as number) > 0.5 ? 1 : 0;
              } else if (sample.user_feedback === 'false_positive') {
                features[featureKey].falsePositive += (value as number) > 0.5 ? 1 : 0;
              }
            });
          }
          
          // Process complexity metrics
          if (extractedFeatures.complexity) {
            Object.entries(extractedFeatures.complexity).forEach(([key, value]) => {
              const featureKey = `complexity_${key}`;
              if (!features[featureKey]) {
                features[featureKey] = { truePositive: 0, falsePositive: 0, correlation: 0, samplesCount: 0 };
              }
              
              features[featureKey].samplesCount++;
              
              if (sample.user_feedback === 'accurate') {
                features[featureKey].truePositive += (value as number) > 0.5 ? 1 : 0;
              } else if (sample.user_feedback === 'false_positive') {
                features[featureKey].falsePositive += (value as number) > 0.5 ? 1 : 0;
              }
            });
          }
          
          // Process pattern metrics
          if (extractedFeatures.patterns) {
            Object.entries(extractedFeatures.patterns).forEach(([key, value]) => {
              const featureKey = `pattern_${key}`;
              if (!features[featureKey]) {
                features[featureKey] = { truePositive: 0, falsePositive: 0, correlation: 0, samplesCount: 0 };
              }
              
              features[featureKey].samplesCount++;
              
              if (sample.user_feedback === 'accurate') {
                features[featureKey].truePositive += (value as number) > 0.5 ? 1 : 0;
              } else if (sample.user_feedback === 'false_positive') {
                features[featureKey].falsePositive += (value as number) > 0.5 ? 1 : 0;
              }
            });
          }
        }
      });
      
      // Calculate correlation for each feature
      Object.keys(features).forEach(key => {
        const total = features[key].truePositive + features[key].falsePositive;
        if (total > 0) {
          features[key].correlation = (features[key].truePositive - features[key].falsePositive) / total;
        }
      });
      
      // Sort features by absolute correlation
      const sortedFeatures = Object.entries(features)
        .filter(([_, stats]) => stats.samplesCount > 5) // Filter for features with enough data
        .sort((a, b) => Math.abs(b[1].correlation) - Math.abs(a[1].correlation))
        .slice(0, 10); // Return top 10 features
      
      return {
        topFeatures: sortedFeatures.map(([name, stats]) => ({
          name,
          correlation: stats.correlation,
          samplesCount: stats.samplesCount
        })),
        analysisDate: new Date().toISOString(),
        sampleSize: data.length
      };
    } catch (error) {
      console.error("Error analyzing feature importance:", error);
      return { error: "Failed to analyze features" };
    }
  }
};

/**
 * Create a hash of the text for deduplication
 */
async function createTextHash(text: string): Promise<string> {
  try {
    // Use a simple hash approach for client-side
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    
    // Convert the hash to a hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    // Fallback if crypto API is not available
    console.warn("Using fallback hash method", error);
    return `hash_${text.length}_${Date.now()}`;
  }
}

/**
 * Extract and process linguistic features from text
 */
function extractLinguisticFeatures(text: string) {
  // Split text into sentences and words
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const words = text.toLowerCase().split(/\s+/).filter(w => w.length > 0);
  const uniqueWords = new Set(words);
  
  // Calculate lexical diversity
  const lexicalDiversity = uniqueWords.size / Math.max(1, words.length);
  
  // Calculate sentence complexity (average length)
  const sentenceLengths = sentences.map(s => s.split(/\s+/).filter(w => w.length > 0).length);
  const avgSentenceLength = sentenceLengths.reduce((sum, len) => sum + len, 0) / Math.max(1, sentences.length);
  
  // Calculate readability using Automated Readability Index (ARI)
  const totalCharacters = words.join('').length;
  const ari = 4.71 * (totalCharacters / Math.max(1, words.length)) + 
              0.5 * (words.length / Math.max(1, sentences.length)) - 21.43;
  
  // Simple sentiment analysis (keyword based)
  const positiveWords = ['good', 'great', 'excellent', 'positive', 'best', 'better', 'amazing', 'love', 'happy'];
  const negativeWords = ['bad', 'worst', 'terrible', 'negative', 'poor', 'worse', 'awful', 'hate', 'sad'];
  
  let positiveCount = 0;
  let negativeCount = 0;
  
  words.forEach(word => {
    if (positiveWords.includes(word)) positiveCount++;
    if (negativeWords.includes(word)) negativeCount++;
  });
  
  const sentimentScore = (positiveCount - negativeCount) / Math.max(1, words.length);
  
  // Simple part-of-speech estimation
  const commonNouns = ['time', 'person', 'year', 'way', 'day', 'thing', 'man', 'world', 'life', 'hand', 'part', 'child'];
  const commonVerbs = ['be', 'have', 'do', 'say', 'get', 'make', 'go', 'know', 'take', 'see', 'come', 'think'];
  const commonAdjectives = ['good', 'new', 'first', 'last', 'long', 'great', 'little', 'own', 'other', 'old', 'right', 'big'];
  
  let nounCount = 0;
  let verbCount = 0;
  let adjectiveCount = 0;
  
  words.forEach(word => {
    if (commonNouns.includes(word)) nounCount++;
    if (commonVerbs.includes(word)) verbCount++;
    if (commonAdjectives.includes(word)) adjectiveCount++;
  });
  
  return {
    lexical_diversity: lexicalDiversity,
    sentence_complexity: avgSentenceLength,
    readability_scores: {
      ari: ari,
      avg_word_length: totalCharacters / Math.max(1, words.length),
      avg_sentence_length: avgSentenceLength
    },
    part_of_speech_distribution: {
      estimated_nouns: nounCount / Math.max(1, words.length),
      estimated_verbs: verbCount / Math.max(1, words.length),
      estimated_adjectives: adjectiveCount / Math.max(1, words.length)
    },
    sentiment_scores: {
      positive_ratio: positiveCount / Math.max(1, words.length),
      negative_ratio: negativeCount / Math.max(1, words.length),
      overall_sentiment: sentimentScore
    }
  };
}
